import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  Input,
  IconButton,
  Stack,
  Alert,
  Skeleton,
  Table
} from '@efast_public/fjd-component-library';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  GovOsResult,
  UeberschreibbareFelderResponse,
  UeberschreibbaresFeld
} from '../api/govos.types';
import { useNotification } from '../shared/notification/useNotification';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache
} from '../api/govos-api';
import { createJoiWithTranslations } from '../utils/joiConfig';
import React, { useEffect, useRef } from 'react';

interface EditUeberschreibbareFelderModalProps {
  isOpen: boolean;
  onClose: () => void;
  feldId: string;
  feldName: string;
  startAppId: number;
  processId: number;
  onShowOriginalValues: () => void;
}

type WertelistenEintragFormData = {
  code: string;
  name: string;
  hilfetext: string;
};

type UeberschreibbareFelderFormData = {
  werte: WertelistenEintragFormData[];
};

const getDefaultValues = (
  feld: UeberschreibbaresFeld | undefined
): UeberschreibbareFelderFormData => {
  if (!feld) {
    return { werte: [] };
  }
  const werteArray =
    feld.ueberschriebeneWerte && feld.ueberschriebeneWerte.length > 0
      ? feld.ueberschriebeneWerte
      : feld.originalWerte;
  if (!werteArray) {
    return { werte: [] };
  }
  return {
    werte: werteArray.map((w) => ({
      code: w.code,
      name: w.name,
      hilfetext: w.hilfetext || ''
    }))
  };
};

export function EditUeberschreibbareFelderModal({
  isOpen,
  onClose,
  feldId,
  feldName,
  startAppId,
  processId,
  onShowOriginalValues
}: EditUeberschreibbareFelderModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const CustomJoi = createJoiWithTranslations(t);

  const { data: response, isLoading } =
    useQuery<UeberschreibbareFelderResponse>({
      queryKey: [
        GovosApiRoutes.ueberschreibbareFelder(startAppId, processId, feldId)
      ],
      queryFn: async () => {
        const response = await govosClient.get<UeberschreibbareFelderResponse>(
          GovosApiRoutes.ueberschreibbareFelder(startAppId, processId, feldId)
        );
        return response.data;
      },
      enabled: isOpen && !!feldId
    });

  const feld = response?.result === 'success' ? response.feld : undefined;

  const validationSchema = CustomJoi.object<UeberschreibbareFelderFormData>({
    werte: CustomJoi.array()
      .items(
        CustomJoi.object({
          code: CustomJoi.string().required(),
          name: CustomJoi.string().required(),
          hilfetext: CustomJoi.string().optional().allow('')
        })
      )
      .min(1)
      .required()
  });

  const { register, handleSubmit, formState, control, reset } =
    useForm<UeberschreibbareFelderFormData>({
      defaultValues: feld ? getDefaultValues(feld) : { werte: [] },
      resolver: joiResolver(validationSchema),
      mode: 'onChange'
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'werte'
  });

  const initializedRef = useRef(false);

  useEffect(() => {
    if (isOpen && feld && !initializedRef.current) {
      reset(getDefaultValues(feld));
      initializedRef.current = true;
    }
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, feld, reset]);

  const { mutate: updateUeberschreibbareFelder, isPending } = useMutation({
    mutationFn: async (data: UeberschreibbareFelderFormData) => {
      return notifyUserFromRequest(
        () =>
          govosClient.put<GovOsResult<unknown>>(
            GovosApiRoutes.updateUeberschreibbareFelder(
              startAppId,
              processId,
              feldId
            ),
            {
              werte: data.werte,
              reset: false
            }
          ),
        {
          successMessage: t('EditUeberschreibbareFelderModal.updateSuccess'),
          errorMessage: t('EditUeberschreibbareFelderModal.updateError'),
          loadingMessage: t('EditUeberschreibbareFelderModal.updateLoading')
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateConfiguredOnlinediensteCache(String(processId))
      });
      void queryClient.invalidateQueries({
        queryKey: [
          GovosApiRoutes.ueberschreibbareFelder(startAppId, processId, feldId)
        ]
      });
      onClose();
    }
  });

  const handleAddRow = () => {
    append({ code: '', name: '', hilfetext: '' });
  };

  const handleRemoveRow = (index: number) => {
    remove(index);
  };

  const handleClearAll = () => {
    remove();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCSVImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const newValues: WertelistenEintragFormData[] = [];

      lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        // Try to parse as CSV (code,name,hilfetext format)
        const parts = trimmedLine.split(',');
        if (parts.length >= 2) {
          newValues.push({
            code: parts[0].trim(),
            name: parts[1].trim(),
            hilfetext: parts[2]?.trim() || ''
          });
        } else {
          // If no comma, use the whole line as both code and name
          newValues.push({
            code: trimmedLine,
            name: trimmedLine,
            hilfetext: ''
          });
        }
      });

      // Replace existing values with imported ones
      remove();
      newValues.forEach((value) => append(value));

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const onSubmit = (data: UeberschreibbareFelderFormData) => {
    updateUeberschreibbareFelder(data);
  };

  if (isLoading || !response) {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title={t('EditUeberschreibbareFelderModal.title', { feldName })}
        size={3}
      >
        <Modal.Content>
          <Stack spacing="xxl" orientation="vertical">
            <Skeleton height={40} />
            <Skeleton height={200} />
            <Skeleton height={300} />
          </Stack>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="editUeberschreibbareFelderModal--width"
      title={t('EditUeberschreibbareFelderModal.title', {
        feldName
      })}
      size={3}
    >
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditUeberschreibbareFelderModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l" orientation="vertical">
          <Alert variant="info">
            {t('EditUeberschreibbareFelderModal.infoText')}
          </Alert>
          <Stack spacing="s" direction="row">
            <Text strong>{t('EditUeberschreibbareFelderModal.feldName')}</Text>
            <Text>{feldName}</Text>
          </Stack>

          <Stack spacing="m" orientation="vertical">
            <Stack
              direction="column"
              spacing="m"
              justifyContent="space-between"
            >
              <Text strong>
                {t('EditUeberschreibbareFelderModal.werteList')}
              </Text>
            </Stack>
            <Stack direction="row" spacing="s" flexWrap="wrap">
              <Button
                variant="primary-text"
                beforeIcon="trashcan"
                size="sm"
                onClick={handleClearAll}
                disabled={fields.length === 0}
              >
                {t('EditUeberschreibbareFelderModal.clearAllButton')}
              </Button>
              <Button
                variant="primary-text"
                beforeIcon="upload"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                {t('EditUeberschreibbareFelderModal.importCSVButton')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCSVImport}
              />
              <Button
                variant="primary-text"
                size="sm"
                beforeIcon="view"
                onClick={onShowOriginalValues}
                disabled={isPending}
              >
                {t('EditUeberschreibbareFelderModal.showOriginalButton')}
              </Button>
            </Stack>
            <Table>
              <Table.Columns sticky>
                <Table.Column value="code">
                  {t('EditUeberschreibbareFelderModal.codeColumn')}
                </Table.Column>
                <Table.Column value="name">
                  {t('EditUeberschreibbareFelderModal.nameColumn')}
                </Table.Column>
                <Table.Column value="hilfetext">
                  {t('EditUeberschreibbareFelderModal.hilfetextColumn')}
                </Table.Column>
                <Table.Column value="actions" />
              </Table.Columns>
              <Table.Rows>
                {fields.map((field, index) => (
                  <Table.Row key={field.id}>
                    <Table.Cell>
                      <Input
                        {...register(`werte.${index}.code`)}
                        placeholder={t(
                          'EditUeberschreibbareFelderModal.codePlaceholder'
                        )}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        {...register(`werte.${index}.name`)}
                        placeholder={t(
                          'EditUeberschreibbareFelderModal.namePlaceholder'
                        )}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        {...register(`werte.${index}.hilfetext`)}
                        placeholder={t(
                          'EditUeberschreibbareFelderModal.hilfetextPlaceholder'
                        )}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <IconButton
                        icon="trashcan"
                        size="sm"
                        onClick={() => handleRemoveRow(index)}
                        label={t(
                          'EditUeberschreibbareFelderModal.deleteRowButton'
                        )}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Rows>
            </Table>

            <div style={{ textAlign: 'left' }}>
              <Button
                variant="primary-text"
                beforeIcon="add"
                onClick={handleAddRow}
                size="sm"
              >
                {t('EditUeberschreibbareFelderModal.addRowButton')}
              </Button>
            </div>

            {formState.errors.werte && (
              <Text variant="error" size="s">
                {t('EditUeberschreibbareFelderModal.werteRequired')}
              </Text>
            )}
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isValid || isPending}
          >
            {t('EditUeberschreibbareFelderModal.saveButton')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
          >
            {t('EditUeberschreibbareFelderModal.cancelButton')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
